import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import isEmpty from "lodash/isEmpty";
import noop from "lodash/noop";
import formatSize from "prettysize";

import { Form, Modal, Upload } from "antd"; // Import Icon from antd

import useCompositeInputHandler from "./compostion-input-image";

import "../../index.css";

const { Item } = Form;

const MAX_IMG_SIZE = 2 ** 18;
const IMG_COUNT_LIMIT = 1;

const dummyRequest = ({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0);

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
const mapErrorStateFromProps = (errors, value, maxNumImages) =>
  value.slice(0, maxNumImages).map((v, i) => ({
    uid: v.uid,
    data: Array.isArray(errors[i]) ? errors[i].map((e) => e) : [],
  }));
/** @type {React.RefForwardingComponent<Props>} */
const ImageInput = forwardRef(
  (
    {
      errors: errorProps = [],
      value: valueProp = [], // Add value prop for initial image data
      formItemProps = {},
      title = "Upload",
      maxImageSize = MAX_IMG_SIZE,
      maxNumImages = IMG_COUNT_LIMIT,
      onChange: onChangeProp = noop,
      ...props
    } = {},
    ref
  ) => {
    // Ensure valueProp is always an array
    const initialImages = Array.isArray(valueProp) ? valueProp : [valueProp];
    const previousErrors = useRef([]);
    const [dateCreated] = useState(Date.now);
    const [errors, setErrors] = useState(() =>
      mapErrorStateFromProps(errorProps, initialImages, maxNumImages)
    );
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");

    // Ensure valueProp is an array
    const valueMemo = useMemo(
      () => (Array.isArray(valueProp) ? valueProp : []),
      [valueProp]
    );

    const onChange = useCallback(
      (value) => {
        if (ref) {
          if (value.length === errors.length) {
            previousErrors.current = errors.slice();
            ref.current.errors = previousErrors.current;
          } else {
            ref.current.errors = previousErrors.current;
          }
        }
       
        onChangeProp(value);
      },
      [errors, ref, onChangeProp]
    );
    const mergeImagesWithFiles = (initialImages, fileList) => {
      const imagesFromFiles = fileList.map((file) => ({
        uid: file.uid,
        lastModified: file.lastModified,
        name: file.name,
        originFileObj: file.originFileObj,
        percent: file.percent,
        response: file.response,
        size: file.size,
        status: file.status,
        thumbUrl: file.preview, // Use file.preview as the thumbUrl for new images
        type: file.type,
      }));
    
      return [...initialImages, ...imagesFromFiles];
    };
    const convertLinkToObject = (link) => {
      if (typeof link === 'string') {
        const name = link.split('/').pop();
        if (!name) {
          throw new Error('Invalid link format. Unable to extract image name.');
        }
    
        return {
          uid: `rc-upload-${Date.now()}`,
          lastModified: Date.now(),
          name: name,
          originFileObj: {
            uid: `rc-upload-${Date.now()}`,
            name: name,
            lastModified: Date.now(),
            size: 0, // You can set the size if you have it available
            type: "image/jpeg", // Set the appropriate type
          },
          percent: 100,
          response: "ok",
          size: 0, // Set the size if you have it available
          status: "done",
          thumbUrl: link, // Set the URL as thumbUrl
          type: "image/jpeg", // Set the appropriate type
        };
      } else if (typeof link === 'object' && link.uid && link.name) {
        // If it's already in the correct format, return it as-is
        return link;
      } else {
        throw new Error('Invalid image format. Expected a string or an object with uid and name.');
      }
    };
    
    // Call the combineImages function and pass the fileList as an argument
    const { inputRef, setValue, value } = useCompositeInputHandler({
      onChange,
      ref,
      value: initialImages, // Pass initialImages here
    });
    
  
    const [imageObject, setImageObject] = useState([]);

    // Use useEffect to watch for changes in the 'value' prop
    useEffect(() => {
      const updatedImages = value.map((link) => {
        try {
          return convertLinkToObject(link);
        } catch (error) {
          console.error(`Error processing link: ${link}`, error.message);
          return null;
        }
      });

      setImageObject(updatedImages.filter((image) => image !== null));
    }, [value]);

    const formattedMaxSize = useMemo(
      () => formatSize(maxImageSize, true, true),
      [maxImageSize]
    );

    const closePreview = useCallback(() => setPreviewVisible(false), []);

 
  

      const openPreview = useCallback(async (file) => {
        setPreviewVisible(true);
        if (!file.url && !file.preview && !file.thumbUrl) {
          // Ensure that file.preview is set by reading the file's data URL
          file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.thumbUrl); // Use file.preview to display the image
      }, []);
    const validateStatus = useMemo(
      () =>
        errors.some(({ data }) => !isEmpty(data))
          ? "error"
          : !isEmpty(value)
          ? "success"
          : "",
      [errors, value]
    );

    const changeHandler = useCallback(
  
      ({ file: { size = null, status = null, uid = null } = {}, fileList }) => {
        if (status === "removed") {
          errors.splice(
            value.findIndex((v) => uid === v.uid),
            1
          );
      
          if (maxNumImages < 2) {
            setErrors(errors.length > 1 ? [errors.pop()] : errors || []);
            setValue(fileList.length > 1 ? [fileList.pop()] : fileList);
            return;
          }
          setErrors(errors);
          setValue(fileList);
          return;
        }

        const error = { uid, data: [] };
        status === "error" &&
          error.data.push({
            message: "No image found in file",
          });
        size > MAX_IMG_SIZE &&
          error.data.push({
            message: `Max: ${formattedMaxSize} - Uploaded: ${formatSize(
              size,
              true,
              true
            )}. Please compress.`,
          });
        if (maxNumImages < 2) {
          setErrors([error]);
          setValue(fileList.length > 1 ? [fileList.pop()] : fileList);
          return;
        }
        setErrors(errors.concat(error));
        setValue(fileList);
      },
      [errors, formattedMaxSize, maxNumImages, setValue, value]
    );

    const help = useMemo(
      () =>
        validateStatus !== "error"
          ? formItemProps.help || null
          : "Invalid image input group detected.",
      [formItemProps.help, validateStatus]
    );

    const className = useMemo(
      () =>
        !isEmpty(props.className)
          ? `_${dateCreated} ${props.className}`
          : `_${dateCreated}`,
      [props.className]
    );

    useLayoutEffect(() => {
      const selectedDisplaySection = document.querySelector("._" + dateCreated);
      if (selectedDisplaySection === null) {
        return;
      }
      selectedDisplaySection
        .querySelectorAll(".ant-upload-list-picture-card-container")
        .forEach((n, i, nodes) => {
          let errorDiv = nodes[i].querySelector(".error-message");
          if (null !== errorDiv) {
            if (!isEmpty(errors[i].data)) {
              errorDiv.innerHTML = errors[i].data.map(
                ({ message }) => `<div>${message}</div>`
              );
            } else {
              errorDiv.remove();
            }
            return;
          }
          if (isEmpty(errors[i].data)) {
            return;
          }
          errorDiv = document.createElement("div");
          errorDiv.innerHTML = errors[i].data.map(
            ({ message }) => `<div>${message}</div>`
          );
          errorDiv.classList.add("error-message");
          nodes[i].appendChild(errorDiv);
        });


    }, [errors]);

    return (
      <Item
        {...{
          ...formItemProps,
          hasFeedback: true,
          validateStatus,
          help,
        }}
      >
        {/* {initialImages.map((imageUrl) => (
          <div key={imageUrl}>
            <img alt="example" style={{ width: "100px" }} src={imageUrl} />
          </div>
        ))} */}

        <Upload
          {...{
            customRequest: dummyRequest,
            listType: "picture-card",
            ...props,
            accept: "image/*",
            fileList: imageObject ,
            className,
            onChange: changeHandler,
            onPreview: openPreview,
            ref: inputRef,
          }}
        >
          {value.length < maxNumImages && (
            <div>
              <div className="ant-upload-text">{title}</div>
            </div>
          )}
        </Upload>
        <Modal visible={previewVisible} footer={null} onCancel={closePreview}>
          <img alt="example" style={{ width: "100%" }} src={previewImage} />
        </Modal>
      </Item>
    );
  }
);

export default ImageInput;

/**
 * @typedef {import("antd/lib/upload").UploadProps & {
 *    accept: never,
 *    errors?: FieldError[][],
 *    fileList: never,
 *    formItemProps?: import("antd/lib/form").FormItemProps & {
 *      hasFeedback: true,
 *      validateStatus: never,
 *      help?: React.ReactNode,
 *    },
 *    listType?: 'picture-card',
 *    maxImageSize?: number = 262144,
 *    maxNumImages?: number = 1,
 *    onChange?: import("../../../../foundation/utils/react/hooks/composite-input-handler").Committer<FileList>,
 *    onPreview: never,
 *    onRemove: never,
 *    value?: FileList = [],
 * }} Props
 */

/** @typedef {import("../../../../foundation/types").FieldError<*>} FieldError */
